import { Calculator } from 'lucide-react';
import type { NodeStruct } from '@/types/node-types';
import {
  CALCULATE_NODE_ID,
  describeCalculateResult,
  getCalculateConfig,
  readCalculateResult,
  type CalculateRunSummary,
} from '@/pages/workflow/helper/calculate';
import { cn } from '@/lib/utils';

/**
 * Calculate read-back: on the node body one compact line per output (the
 * formula before a run, the value after), and in the result panel a card
 * that shows, per output, the formula, the value, and the inputs it read.
 */

const show = (value: unknown, max = 48): string => {
  const text =
    value === undefined
      ? '—'
      : typeof value === 'string'
        ? JSON.stringify(value)
        : (JSON.stringify(value) ?? String(value));
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export const isCalculateResult = (result: unknown): boolean =>
  readCalculateResult(result) !== null;

export function CalculateResultCard({
  summary,
}: {
  summary: CalculateRunSummary;
}) {
  const rows =
    summary.trace.length > 0
      ? summary.trace
      : Object.entries(summary.result).map(([name, value]) => ({
          name,
          formula: null,
          value,
          inputs: [],
        }));
  return (
    <div
      className='rounded-md border border-border bg-muted-background/40 p-3 text-sm'
      data-testid='calculate-result-card'
    >
      <div className='flex items-center gap-2 mb-2'>
        <Calculator className='size-4 text-muted-foreground' />
        <span className='font-medium'>
          {summary.error
            ? 'Calculate stopped'
            : `Calculated ${rows.length} output${rows.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {summary.error && (
        <div
          className='mb-2 rounded border border-destructive/40 bg-destructive/5 p-2 text-xs'
          data-testid='calculate-result-error'
        >
          <p className='text-destructive'>
            {summary.error.outputName
              ? `${summary.error.outputName}: ${summary.error.message}`
              : summary.error.message}
          </p>
          {summary.error.hint && (
            <p className='text-muted-foreground mt-0.5'>{summary.error.hint}</p>
          )}
        </div>
      )}
      <ul className='space-y-2'>
        {rows.map((row) => (
          <li
            key={row.name}
            className='rounded border border-border/60 bg-background p-2'
            data-testid='calculate-result-row'
          >
            <div className='flex items-baseline gap-2 font-mono text-xs'>
              <span className='font-semibold'>{row.name}</span>
              <span className='text-muted-foreground'>=</span>
              <span className='truncate' title={JSON.stringify(row.value)}>
                {show(row.value, 80)}
              </span>
            </div>
            {row.formula && (
              <p className='mt-1 font-mono text-[11px] text-muted-foreground break-all'>
                {row.formula}
              </p>
            )}
            {row.inputs.length > 0 && (
              <dl className='mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]'>
                {row.inputs.map((input) => (
                  <div key={input.field} className='contents'>
                    <dt className='font-mono text-muted-foreground'>
                      {input.field}
                    </dt>
                    <dd
                      className={cn(
                        'font-mono truncate',
                        input.missing && 'italic text-amber-600',
                      )}
                      title={
                        input.missing ? 'missing' : JSON.stringify(input.value)
                      }
                    >
                      {input.missing ? 'missing' : show(input.value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        ))}
      </ul>
      {summary.truncated && (
        <p className='mt-2 text-[11px] text-muted-foreground'>
          The trace was shortened to stay under 16 KB; values are complete in
          the record below.
        </p>
      )}
    </div>
  );
}

export function CalculateResultView({ result }: { result: unknown }) {
  const summary = readCalculateResult(result);
  if (!summary) return null;
  return (
    <div className='mb-3' data-testid='calculate-result-view'>
      <CalculateResultCard summary={summary} />
    </div>
  );
}

/**
 * Node body: one compact line per output (`score = round(a * b, 2)`), and
 * after a run a chip with the first output's value.
 */
export function CalculateNodeRows({
  struct,
  result,
}: {
  struct: NodeStruct;
  result: unknown;
}) {
  if (struct.node_id !== CALCULATE_NODE_ID) return null;
  const config = getCalculateConfig(struct);
  const summary = readCalculateResult(result);
  const outputs = config.outputs.filter((o) => o.name || o.formula);
  return (
    <div className='px-4 pt-1 space-y-1' data-testid='calculate-node-rows'>
      {outputs.length === 0 ? (
        <p className='text-[11px] text-muted-foreground italic'>
          no outputs yet
        </p>
      ) : (
        <ul className='space-y-0.5'>
          {outputs.slice(0, 6).map((output, index) => {
            const value = summary?.result[output.name];
            return (
              <li
                key={`${output.name}-${index}`}
                className='flex items-baseline gap-1 font-mono text-[11px] leading-tight'
                title={output.formula}
              >
                <span className='font-semibold text-foreground truncate max-w-[45%]'>
                  {output.name || '?'}
                </span>
                <span className='text-muted-foreground'>=</span>
                <span className='text-muted-foreground truncate'>
                  {summary && value !== undefined
                    ? show(value, 40)
                    : output.formula || '…'}
                </span>
              </li>
            );
          })}
          {outputs.length > 6 && (
            <li className='text-[11px] text-muted-foreground'>
              +{outputs.length - 6} more
            </li>
          )}
        </ul>
      )}
      {summary && (
        <span
          className={cn(
            'inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white',
            summary.error ? 'bg-destructive' : 'bg-emerald-600',
          )}
          data-testid='calculate-node-chip'
          title={describeCalculateResult(summary)}
        >
          <Calculator className='size-3 shrink-0' />
          <span className='truncate'>{describeCalculateResult(summary)}</span>
        </span>
      )}
    </div>
  );
}
